/* ============================================================
   HANAN BAKRI - EDITORIAL LAYER BEHAVIOUR
   ------------------------------------------------------------
   Two effects the live site has and Horizon does not:

     1. Scroll reveal   - fade + rise as an element enters view
     2. Column parallax - three plate columns drifting at
                          different rates across the viewport

   The hero's slow zoom is CSS (@keyframes hb-ed-slow-zoom in
   hb-editorial.css), so it is not duplicated here.

   Dependency-free. No globals beyond window.hbEditorial.

   Opt-in only. Nothing is auto-applied to Horizon's own markup;
   elements must carry .hb-ed-reveal or [data-hb-parallax].

   Both effects move on the block axis only, so nothing here is
   direction-sensitive and the file needs no RTL branch.

   Load with `defer` after hb-editorial.css.
   ============================================================ */

(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The hidden half of the reveal lives behind this class, so a
     page whose JS is blocked or fails to parse renders fully
     visible instead of blank. Set before first paint work. */
  root.classList.add('hb-ed-js');

  /* ----------------------------------------------------------
     1. SCROLL REVEAL
     One observer for the document. Elements unobserve once
     revealed, so a long page never accumulates callbacks.
     ---------------------------------------------------------- */

  var REVEAL_SELECTOR = '.hb-ed-reveal';
  var observer = null;

  function showNow(el) {
    el.classList.add('is-visible');
  }

  function getObserver() {
    if (observer) return observer;

    if (!('IntersectionObserver' in window)) return null;

    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          if (entries[i].isIntersecting) {
            showNow(entries[i].target);
            observer.unobserve(entries[i].target);
          }
        }
      },
      /* threshold 0, not 0.12.

         Measured on the live homepage: scrolling the full page at roughly one
         viewport per 60ms - a phone flick, not an unusual gesture - left 15 of
         17 revealed elements still at opacity 0, because a fast scroll can
         carry an element past the 12% mark between two observer samples. At a
         reading pace all 17 fired. A reveal that fails leaves a band of the
         page permanently blank, which is a far worse outcome than a reveal
         that starts a fraction early.

         The positive rootMargin arms each element 240px before it reaches the
         viewport, so the animation is already running when it arrives. That is
         also closer to the intent: the reveal should greet the reader, not
         perform for her.
     
         .hb-mo-veil needs none of this - it is a scroll-linked CSS animation
         whose opacity is a pure function of scroll position, so it has no
         hidden state that can get stuck. */
      { threshold: 0, rootMargin: '240px 0px 240px 0px' }
    );

    return observer;
  }

  function initReveal(scope) {
    var targets = (scope || document).querySelectorAll(REVEAL_SELECTOR);
    if (!targets.length) return;

    var obs = reduceMotion.matches ? null : getObserver();

    for (var i = 0; i < targets.length; i += 1) {
      var el = targets[i];
      if (el.classList.contains('is-visible')) continue;

      if (!obs) {
        /* No IntersectionObserver, or the reader asked for less
           motion: show everything immediately. */
        showNow(el);
        continue;
      }

      /* Anything already on screen at init reveals at once
         rather than waiting for a scroll that may never come. */
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        showNow(el);
        continue;
      }

      obs.observe(el);
    }
  }

  /* ----------------------------------------------------------
     2. COLUMN PARALLAX
     Writes --hb-ed-par-1..3 onto each [data-hb-parallax] block.
     The CSS applies them as translate3d on the block axis.

     Disabled below 901px and under reduced motion, matching the
     breakpoints in hb-editorial.css so the two never disagree.
     ---------------------------------------------------------- */

  var PARALLAX_SELECTOR = '[data-hb-parallax]';
  var galleries = [];
  var ticking = false;
  var scrollBound = false;

  /* Resting offset and travel per column, from the live design */
  var LAYERS = [
    { rest: -20, travel: -170 },
    { rest: -140, travel: 140 },
    { rest: -10, travel: -145 }
  ];

  function updateParallax() {
    var disabled = reduceMotion.matches || window.innerWidth < 901;

    for (var i = 0; i < galleries.length; i += 1) {
      var el = galleries[i];

      if (disabled) {
        for (var d = 0; d < LAYERS.length; d += 1) {
          el.style.setProperty('--hb-ed-par-' + (d + 1), '0px');
        }
        continue;
      }

      var rect = el.getBoundingClientRect();

      /* 0 as the block enters from below, 1 as it leaves above */
      var span = window.innerHeight + rect.height;
      var progress = span > 0 ? (window.innerHeight - rect.top) / span : 0;
      progress = Math.min(Math.max(progress, 0), 1);

      var accelerated = (progress - 0.5) * 1.5;

      for (var n = 0; n < LAYERS.length; n += 1) {
        var offset = Math.round(LAYERS[n].rest + accelerated * LAYERS[n].travel);
        el.style.setProperty('--hb-ed-par-' + (n + 1), offset + 'px');
      }
    }
  }

  function requestParallaxUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      updateParallax();
      ticking = false;
    });
  }

  function initParallax(scope) {
    var found = (scope || document).querySelectorAll(PARALLAX_SELECTOR);
    if (!found.length) return;

    for (var i = 0; i < found.length; i += 1) {
      if (galleries.indexOf(found[i]) === -1) galleries.push(found[i]);
    }

    if (!scrollBound) {
      window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
      window.addEventListener('resize', requestParallaxUpdate, { passive: true });
      scrollBound = true;
    }

    updateParallax();
  }

  /* Drop nodes the theme editor has removed, so the scroll
     handler never walks a detached tree. */
  function pruneParallax() {
    galleries = galleries.filter(function (el) {
      return el.isConnected;
    });
  }

  /* ----------------------------------------------------------
     3. INIT
     ---------------------------------------------------------- */

  function init(scope) {
    initReveal(scope);
    initParallax(scope);
  }

  function onMotionPreferenceChange() {
    if (reduceMotion.matches) {
      /* Reveal everything still pending and stop the drift. */
      var pending = document.querySelectorAll(REVEAL_SELECTOR + ':not(.is-visible)');
      for (var i = 0; i < pending.length; i += 1) showNow(pending[i]);
      if (observer) observer.disconnect();
      observer = null;
    } else {
      initReveal(document);
    }
    updateParallax();
  }

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', onMotionPreferenceChange);
  } else if (typeof reduceMotion.addListener === 'function') {
    reduceMotion.addListener(onMotionPreferenceChange);
  }

  /* Horizon merchants add, move and reload sections live in the
     theme editor. Re-scan the section that changed. */
  document.addEventListener('shopify:section:load', function (event) {
    pruneParallax();
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', pruneParallax);

  document.addEventListener('shopify:block:select', function (event) {
    if (event.target && event.target.classList.contains('hb-ed-reveal')) {
      showNow(event.target);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Exposed so a section script can register markup it injects
     after load (filtered grids, paginated collections). */
  window.hbEditorial = {
    init: init,
    refresh: function () {
      pruneParallax();
      init(document);
    }
  };
})();
